import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useDeleteLifeInsuranceTariffGroup,
  useFilterLifeInsuranceTariffGroups,
} from "../../../../common/api/queries/lifeInsuranceTariff";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../common/constants";
import { SearchPageRequest } from "../../../../common/types";
import { LifeInsuranceTariffGroup } from "../../types";

interface Props {
  searchParams: SearchPageRequest;
  onUpdateClick: (group: LifeInsuranceTariffGroup) => void;
}

export const LifeInsuranceTariffGroupTable = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isFetching } = useFilterLifeInsuranceTariffGroups(searchParams);
  const deleteTariffGroup = useDeleteLifeInsuranceTariffGroup();

  const columns: ColumnsType<LifeInsuranceTariffGroup> = [
    {
      key: "name",
      title: t("lifeInsuranceTariff.attrs.name"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <div className="nowrap">
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("lifeInsuranceTariff.helpers.tariffGroupDeleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => deleteTariffGroup.mutate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Table<LifeInsuranceTariffGroup>
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.SMALL }}
      dataSource={data?.pageData}
      loading={isFetching}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
